import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../lib/api/apiService';
import { getCartQueryKey, useCartQuery } from './useCartQuery';
import {
  getCartItemsArrayFromCartApiResponse,
  normalizeServerItemToCartItem,
} from '../utils/productHelpers';
import useCurrentUser from './useCurrentUser';

/**
 * Hook personnalisé pour gérer la synchronisation du panier avec l'API
 * @returns {Object} État et fonctions de synchronisation
 */
const useCartSync = ({ clearCart, addItem }) => {
  const queryClient = useQueryClient();
  const hasSyncedRef = useRef(false);
  const lastSyncedDataRef = useRef(null);

  const { data: user } = useCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  const userId = user?._id || user?.id;

  // Récupérer le panier depuis l'API
  const { data: cartData, isLoading, error } = useCartQuery(userId, {
    enabled: isAuthenticated && !!userId,
  });

  // Synchroniser le panier local avec le panier du serveur
  useEffect(() => {
    if (!isAuthenticated || !userId || isLoading || !cartData) {
      return;
    }

    // Vérifier si on a déjà synchronisé ces données
    const cartDataString = JSON.stringify(cartData);
    if (hasSyncedRef.current && lastSyncedDataRef.current === cartDataString) {
      return;
    }

    const serverCartItems = getCartItemsArrayFromCartApiResponse(cartData);

    if (Array.isArray(serverCartItems)) {
      // Marquer comme synchronisé
      hasSyncedRef.current = true;
      lastSyncedDataRef.current = cartDataString;

      // Vider le panier local pour éviter les doublons
      clearCart();

      // Ajouter chaque item du panier serveur au panier local
      serverCartItems.forEach((serverItem) => {
        const normalizedItem = normalizeServerItemToCartItem(serverItem);
        if (normalizedItem) {
          addItem(normalizedItem);
        }
      });
    }
  }, [cartData, isAuthenticated, userId, isLoading, clearCart, addItem]);

  // Réinitialiser le flag de synchronisation si l'utilisateur change
  useEffect(() => {
    hasSyncedRef.current = false;
    lastSyncedDataRef.current = null;
  }, [userId]);

  // Fonction pour invalider le cache et resynchroniser
  const refetchCart = () => {
    hasSyncedRef.current = false;
    lastSyncedDataRef.current = null;
    queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
  };

  return {
    cartData,
    isLoading,
    error,
    refetchCart,
  };
};

export default useCartSync;

